import { apiClient } from '../client';
import { API_ENDPOINTS } from '../../utils/constants';
import {
  QRCodeCreate,
  QRCodeUpdate,
  QRCodeResponse,
  QRCodeListResponse,
  QRCodeDetailResponse,
  QRTagCreate,
  QRTagResponse
} from '../../types/qr.types';

export const qrApi = {
  // QR Code CRUD
  bulkAction: async (ids: number[], action: 'activate' | 'deactivate' | 'delete'): Promise<{
    ids: number[]; action: 'activate' | 'deactivate' | 'delete'; affected_count: number;
  }> => {
    const response = await apiClient.post(`${API_ENDPOINTS.QR.CODES}/bulk`, { ids, action });
    return response.data;
  },

  createQRCode: async (data: QRCodeCreate): Promise<QRCodeResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.QR.CODES, data);
    return response.data;
  },

  getQRCodes: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    has_rules?: boolean;
    tag_id?: number;
  }): Promise<QRCodeListResponse> => {
    const response = await apiClient.get(API_ENDPOINTS.QR.CODES, { params });
    return response.data;
  },

  getQRCode: async (identifier: string | number): Promise<QRCodeDetailResponse> => {
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}`);
    return response.data;
  },

  updateQRCode: async (identifier: string | number, data: QRCodeUpdate): Promise<QRCodeResponse> => {
    const response = await apiClient.put(`${API_ENDPOINTS.QR.CODES}/${identifier}`, data);
    return response.data;
  },

  deleteQRCode: async (identifier: string | number): Promise<void> => {
    await apiClient.delete(`${API_ENDPOINTS.QR.CODES}/${identifier}`);
  },

  // QR Code Generation
  generateQRImage: async (identifier: string | number, size: string = 'medium', format: string = 'PNG'): Promise<Blob> => {
    // Add timestamp to bust cache
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}/generate`, {
      params: { 
        size, 
        format,
        _t: Date.now() // Cache buster
      },
      responseType: 'blob'
    });
    return response.data;
  },

  getQRUrl: async (identifier: string | number): Promise<{ url: string; short_code: string }> => {
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}/url`);
    return response.data;
  },

  // Tag Management
  createTag: async (data: QRTagCreate): Promise<QRTagResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.QR.TAGS, data);
    return response.data;
  },

  getTags: async (): Promise<QRTagResponse[]> => {
    const response = await apiClient.get(API_ENDPOINTS.QR.TAGS);
    return response.data;
  },

  updateTag: async (id: number, data: Partial<QRTagCreate>): Promise<QRTagResponse> => {
    const response = await apiClient.put(`${API_ENDPOINTS.QR.TAGS}/${id}`, data);
    return response.data;
  },

  deleteTag: async (id: number): Promise<void> => {
    await apiClient.delete(`${API_ENDPOINTS.QR.TAGS}/${id}`);
  },

  // Analytics endpoints
  getAnalyticsSummary: async (identifier: string | number): Promise<any> => {
    // Pass browser's timezone for accurate time-based aggregations
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}/analytics/summary`, {
      params: { timezone }
    });
    return response.data;
  },

  getAnalytics: async (identifier: string | number, params?: {
    period?: string;
    granularity?: string;
  }): Promise<any> => {
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}/analytics`, { params });
    return response.data;
  },

  getRealtimeAnalytics: async (identifier: string | number): Promise<any> => {
    const response = await apiClient.get(`${API_ENDPOINTS.QR.CODES}/${identifier}/analytics/realtime`);
    return response.data;
  },
};
